import { motion } from 'framer-motion';
import { PublicLayout } from './BookPage';
import { useI18n } from '../i18n/I18nContext';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Quote, MessageCircleQuestion } from 'lucide-react';

const spring = { type: 'spring', stiffness: 300, damping: 20 };

export default function FaqPage() {
  const { t } = useI18n();
  const qa = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((i) => ({ q: t(`faq.q${i}`), a: t(`faq.a${i}`) }));
  const testimonials = [1, 2, 3].map((i) => ({ quote: t(`faq.t${i}Quote`), author: t(`faq.t${i}Author`) }));

  return (
    <PublicLayout testid="faq-page" pageKey="page_faq">
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={spring} className="max-w-3xl">
        <p className="text-or-400 font-bold tracking-widest text-sm uppercase">{t('faq.eyebrow')}</p>
        <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-creme mt-3 flex items-center gap-3">
          <MessageCircleQuestion className="w-9 h-9 text-or-400 shrink-0" strokeWidth={2.5} />
          {t('faq.title')}
        </h1>
        <p className="text-base sm:text-lg text-brume leading-relaxed mt-6">{t('faq.intro')}</p>
      </motion.div>

      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ ...spring, delay: 0.15 }}
        className="bg-nuit-800 border border-nuit-700 rounded-3xl p-6 sm:p-8 mt-10" data-testid="faq-list">
        <Accordion type="single" collapsible className="w-full">
          {qa.map((item, i) => (
            <AccordionItem key={i} value={`q${i}`} className="border-nuit-700" data-testid={`faq-item-${i + 1}`}>
              <AccordionTrigger className="text-left font-bold text-creme hover:text-or-300 text-base">
                {item.q}
              </AccordionTrigger>
              <AccordionContent className="text-brume leading-relaxed text-base">
                {item.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </motion.div>

      <h2 className="text-2xl font-semibold text-creme mt-14 mb-6" data-testid="testimonials-title">
        {t('faq.testimonialsTitle')}
      </h2>
      <div className="grid sm:grid-cols-3 gap-6">
        {testimonials.map((tm, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
            transition={{ ...spring, delay: 0.2 + i * 0.08 }}
            className="bg-nuit-800 border border-nuit-700 rounded-3xl p-8 hover:border-or-400/40 transition-all"
            data-testid={`testimonial-${i + 1}`}>
            <Quote className="w-6 h-6 text-or-400 mb-4" strokeWidth={2.5} />
            <p className="text-base text-creme leading-relaxed italic">« {tm.quote} »</p>
            <p className="text-sm font-bold text-brume mt-4">{tm.author}</p>
          </motion.div>
        ))}
      </div>
    </PublicLayout>
  );
}
